/*LIBRARY*/
import { connect } from "react-redux";
import { useState, useRef, useLayoutEffect, useMemo, useEffect } from "react";

/*COMPONENT*/
import Modal from "../common_modal/Modal";
import CheckBoxNew from "../common_input/CheckBoxNew";
import SELECTION_REF from "./SELECTION_REF";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";

/*PICTURE*/
import menu_icon from "../../Assets/svg/menu_icon.svg";

/*FUNCTION*/

/*DATA*/
import dict from "../../Data/dict.json";

/*CONST*/

const SET_AT = ({
  //variable
  auth,
  layer,
  properties,

  //function
  set_value_properties,
}) => {
  //local storage
  const language = localStorage?.language || auth?.language;

  //state & ref
  const array_templates_ref = useRef(null);
  const should_focus_ref = useRef(false);
  const csv_file_ref = useRef(null);

  // Animation Refs
  const prev_rects_ref = useRef({});
  const [is_animating, set_is_animating] = useState(false);

  // Reorder State
  const [drag_source_index, set_drag_source_index] = useState(null);
  const [drop_target_index, set_drop_target_index] = useState(null);
  const [modal_selection_ref, set_modal_selection_ref] = useState(false);

  // Input Mode State: "list" or "textarea" initialized from local storage
  const [input_mode, set_input_mode] = useState(
    localStorage?.getItem("array_input_mode") || "list",
  );

  // Sync input_mode with local storage whenever it changes
  useEffect(() => {
    localStorage?.setItem("array_input_mode", input_mode);
  }, [input_mode]);

  //global props
  const { layer_id, geo_layer_list } = layer;
  const { field_editable } = properties;

  //variable
  const array_templates = useMemo(() => {
    return field_editable?.array_templates || [];
  }, [field_editable]);

  const parent_array_templates_key = field_editable?.parent_array_templates_key;
  const geo_layer = geo_layer_list.find(
    (item) => item?.geo_layer?._id === layer_id,
  )?.geo_layer;
  const fields = geo_layer?.fields || [];
  let parent_field;
  let opsi_parent_array = [];
  if (parent_array_templates_key) {
    parent_field = fields.find(
      (item) => item.key === parent_array_templates_key,
    );
    opsi_parent_array = parent_field?.array_templates || [];
  }

  // --- ANIMATION LOGIC (FLIP with Freeze) ---
  const snapshot_positions = () => {
    if (!array_templates_ref.current) return;
    const items = array_templates_ref.current.querySelectorAll("[data-id]");
    const rects = {};
    items.forEach((item) => {
      const id = item.dataset.id;
      if (id) {
        rects[id] = item.getBoundingClientRect();
      }
    });
    prev_rects_ref.current = rects;
  };

  useLayoutEffect(() => {
    if (!is_animating || !array_templates_ref.current) return;

    const items = array_templates_ref.current.querySelectorAll("[data-id]");
    const moved_items = [];

    // 1. FIRST & LAST & INVERT
    items.forEach((item) => {
      const id = item.dataset.id;
      const old_rect = prev_rects_ref.current[id];
      const new_rect = item.getBoundingClientRect();

      if (old_rect && new_rect) {
        const delta_y = old_rect.top - new_rect.top;

        if (Math.abs(delta_y) > 0) {
          item.style.transform = `translateY(${delta_y}px)`;
          item.style.transition = "none";
          item.style.zIndex = "100";
          moved_items.push(item);
        }
      }
    });

    // 2. FREEZE & PLAY
    const animation_start_timer = setTimeout(() => {
      moved_items.forEach((item) => {
        item.style.transition = "transform 600ms cubic-bezier(0.2, 0, 0, 1)";
        item.style.transform = "translateY(0)";
      });
    }, 150);

    // 3. CLEANUP
    const cleanup_timer = setTimeout(() => {
      set_is_animating(false);
      items.forEach((item) => {
        item.style.transform = "";
        item.style.transition = "";
        item.style.zIndex = "";
      });
    }, 150 + 600);

    return () => {
      clearTimeout(animation_start_timer);
      clearTimeout(cleanup_timer);
    };
  }, [array_templates, is_animating]);

  //functions
  const handle_add_array_template = () => {
    should_focus_ref.current = true;

    const new_array = [...array_templates];
    new_array.push({
      name: "",
      parent: "",
    });

    set_value_properties({
      key: "field_editable",
      value: {
        ...field_editable,
        array_templates: new_array,
      },
    });
    setTimeout(() => {
      if (array_templates_ref.current) {
        array_templates_ref.current.scrollTop =
          array_templates_ref.current.scrollHeight;
      }
    }, 0);
  };

  const handle_upload_csv = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split("\n").filter((line) => line.trim() !== "");

      if (lines.length === 0) return;

      let delimiter = ",";
      if (lines[0].includes(";")) {
        delimiter = ";";
      }

      const headers = lines[0].split(delimiter).map((header) => header.trim());

      if (headers.length < 1) {
        alert("CSV must contain at least one column for the main name.");
        event.target.value = null;
        return;
      }

      const name_index = 0;
      const parent_index = headers.length > 1 ? 1 : -1;

      const new_array_templates = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(delimiter);
        const item_name = values[name_index]?.trim() || "";
        const parent_name =
          parent_index !== -1 ? values[parent_index]?.trim() || "" : "";

        if (item_name) {
          new_array_templates.push({
            name: item_name,
            parent: parent_name,
          });
        }
      }

      if (new_array_templates.length > 0) {
        set_value_properties({
          key: "field_editable",
          value: {
            ...field_editable,
            array_templates: new_array_templates,
          },
        });
      }

      event.target.value = null;
    };

    reader.readAsText(file);
  };

  const handle_download_csv_example = () => {
    let csv_content = "";
    if (field_editable?.is_use_parent_array_template) {
      csv_content = "Child;Parent\n";
      csv_content += "Item A;Group One\n";
      csv_content += "Item B;Group One\n";
      csv_content += "Item C;Group One\n";
    } else {
      csv_content = "Nilai\n";
      csv_content += "Rendah\n";
      csv_content += "Sedang\n";
      csv_content += "Tinggi";
    }
    const blob = new Blob([csv_content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "array_templates_example.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- REORDER LOGIC ---
  const handle_drag_start = (e, index) => {
    e.stopPropagation();

    // 1. Mandatory Data Set for Chrome
    e.dataTransfer.setData("text/plain", "" + index);
    e.dataTransfer.effectAllowed = "move";

    // 2. Set Drag Image (Visuals)
    const row = e.target.closest(".folder_wrapper");
    if (row && e.dataTransfer.setDragImage) {
      // Center the ghost image roughly under the cursor
      e.dataTransfer.setDragImage(row, 10, 10);
    }

    // 3. THE CHROME FIX: Defer the state update
    // If you update state immediately, React re-renders and changes opacity/DOM
    // which causes Chrome to lose the drag reference immediately.
    setTimeout(() => {
      set_drag_source_index(index);
    }, 0);
  };

  const handle_drag_over = (e, index) => {
    // 4. Always prevent default to allow Drop
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    if (drop_target_index !== index) {
      set_drop_target_index(index);
    }
  };

  const handle_drop = (e, target_index) => {
    e.preventDefault();
    e.stopPropagation();

    // Safety check if state hasn't updated yet (rare but possible)
    if (drag_source_index === null) {
      // Fallback: try to read from dataTransfer if state is empty
      const data_idx = e.dataTransfer.getData("text/plain");
      if (data_idx) {
        const idx = parseInt(data_idx);
        // Proceed with idx
        if (!isNaN(idx)) {
          snapshot_positions();
          const new_array = [...array_templates];
          const [removed] = new_array.splice(idx, 1);
          let final_target = target_index;
          if (idx < target_index) {
            final_target = target_index - 1;
          }
          new_array.splice(final_target, 0, removed);
          set_value_properties({
            key: "field_editable",
            value: { ...field_editable, array_templates: new_array },
          });
          set_is_animating(true);
        }
      }
      handle_drag_end();
      return;
    }

    snapshot_positions();

    const new_array = [...array_templates];
    const [removed] = new_array.splice(drag_source_index, 1);

    let final_target = target_index;
    if (drag_source_index < target_index) {
      final_target = target_index - 1;
    }

    new_array.splice(final_target, 0, removed);

    set_value_properties({
      key: "field_editable",
      value: {
        ...field_editable,
        array_templates: new_array,
      },
    });

    set_is_animating(true);
    handle_drag_end();
  };

  const handle_drag_end = () => {
    set_drag_source_index(null);
    set_drop_target_index(null);
  };

  // Dual Textarea Sync Handlers
  const handle_child_textarea_change = (e) => {
    const child_lines = e.target.value.split("\n");
    const parent_lines = array_templates.map((item) => item.parent || "");
    const length = Math.max(child_lines.length, parent_lines.length);

    const new_array = Array.from({ length }).map((_, i) => ({
      name: child_lines[i] || "",
      parent: parent_lines[i] || "",
    }));

    set_value_properties({
      key: "field_editable",
      value: { ...field_editable, array_templates: new_array },
    });
  };

  const handle_parent_textarea_change = (e) => {
    const parent_lines = e.target.value.split("\n");
    const child_lines = array_templates.map((item) => item.name || "");
    const length = Math.max(child_lines.length, parent_lines.length);

    const new_array = Array.from({ length }).map((_, i) => ({
      name: child_lines[i] || "",
      parent: parent_lines[i] || "",
    }));

    set_value_properties({
      key: "field_editable",
      value: { ...field_editable, array_templates: new_array },
    });
  };

  const modal_selection_ref_content = modal_selection_ref && (
    <Modal
      modalSize="small"
      id="modal"
      isOpen={modal_selection_ref}
      onClose={() => {
        set_modal_selection_ref(false);
      }}
    >
      <SELECTION_REF set_modal_selection_ref={set_modal_selection_ref} />
    </Modal>
  );

  return (
    <main>
      {modal_selection_ref_content}
      <p className="text_medium sub_color">
        {dict?.["Fill option values"]?.[language]}
      </p>
      <section className="two_container_auto margin_bottom">
        <CheckBoxNew
          title={dict?.["Use the reference column"]?.[language]}
          value={field_editable?.is_use_parent_array_template}
          handle={() => {
            if (!field_editable?.is_use_parent_array_template) {
              set_modal_selection_ref(true);
              set_value_properties({
                key: "field_editable",
                value: {
                  ...field_editable,
                  is_use_parent_array_template: true,
                },
              });
            } else {
              set_value_properties({
                key: "field_editable",
                value: {
                  ...field_editable,
                  parent_array_templates_key: "",
                  is_use_parent_array_template: false,
                },
              });
            }
          }}
        />
        <div className="text_right">
          {parent_field?.key && (
            <p
              className="badge background_white cursor_pointer"
              onClick={() => {
                set_modal_selection_ref(true);
              }}
            >
              {parent_field?.name}
            </p>
          )}
        </div>
      </section>

      <section className="container background_white margin_bottom">
        <div className="two_container_equal margin_bottom">
          <div>
            <input
              type="file"
              accept=".csv"
              ref={csv_file_ref}
              style={{ display: "none" }}
              onChange={handle_upload_csv}
            />
            <button
              className="button button_white outline_grey"
              onClick={() => csv_file_ref.current.click()}
            >
              {dict?.["Upload CSV list"]?.[language]}
            </button>
          </div>

          <div className="text_right">
            <button
              className="button button_white outline_grey"
              onClick={handle_download_csv_example}
            >
              {dict?.["CSV Template Standard"]?.[language]}
            </button>
          </div>
        </div>

        {language === "eng" ? (
          <>
            <p className="text_medium sub_color margin_bottom">
              To ensure your data is processed correctly, please download and
              use our{" "}
              <b
                className="text_black cursor_pointer"
                onClick={handle_download_csv_example}
              >
                Standard Template.
              </b>
            </p>
            <p className="text_medium sub_color">
              <b className="text_black">Important:</b> Please do not rename or
              delete the column header.
            </p>
          </>
        ) : (
          <>
            <p className="text_medium sub_color margin_bottom">
              Untuk memastikan data Anda diproses dengan benar, silakan unduh
              dan gunakan{" "}
              <b
                className="text_black cursor_pointer"
                onClick={handle_download_csv_example}
              >
                Template Standar
              </b>{" "}
              kami.
            </p>
            <p className="text_medium sub_color">
              <b className="text_black">Penting:</b> Jangan mengubah nama atau
              menghapus header kolom.
            </p>
          </>
        )}
      </section>

      <section className="container background_white">
        <section className="margin_bottom">
          {parent_array_templates_key ? (
            <div className="two_container_equal">
              <p className="text_medium">{dict?.["Value"]?.[language]}</p>
              <p className="text_medium text_right">
                {dict?.["Shown if"]?.[language]}
              </p>
            </div>
          ) : (
            <div className="two_container_equal">
              <p className="text_medium">
                {dict?.["Fill-in values"]?.[language]}
              </p>
            </div>
          )}

          <CheckBoxNew
            title={dict?.["Allow other answers"]?.[language]}
            value={field_editable?.isAllowOtherAnswer}
            handle={() => {
              if (!field_editable?.isAllowOtherAnswer) {
                set_value_properties({
                  key: "field_editable",
                  value: {
                    ...field_editable,
                    isAllowOtherAnswer: true,
                  },
                });
              } else {
                set_value_properties({
                  key: "field_editable",
                  value: {
                    ...field_editable,
                    isAllowOtherAnswer: false,
                  },
                });
              }
            }}
          />
        </section>

        {/* --- INPUT MODE TOGGLE (Always Visible Now) --- */}
        <div
          className="two_container_auto margin_bottom margin_top"
          style={{ justifyContent: "space-between" }}
        >
          <p className="text_medium">
            {language === "eng" ? "Input Mode:" : "Mode Input:"}
          </p>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className={`button_small ${input_mode === "list" ? "background_black text_white" : "button_white outline_grey"}`}
              onClick={() => set_input_mode("list")}
            >
              {language === "eng" ? "List" : "Daftar"}
            </button>
            <button
              className={`button_small ${input_mode === "textarea" ? "background_black text_white" : "button_white outline_grey"}`}
              onClick={() => set_input_mode("textarea")}
            >
              {language === "eng" ? "Textarea" : "Area Teks"}
            </button>
          </div>
        </div>

        {input_mode === "textarea" ? (
          field_editable?.is_use_parent_array_template ? (
            // --- DUAL TEXTAREA MODE (WITH REFERENCE) ---
            <div
              style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}
            >
              <div style={{ flex: 1 }}>
                <p
                  className="text_medium margin_bottom"
                  style={{ fontWeight: "bold" }}
                >
                  {dict?.["Value"]?.[language] || "Child Value"}
                </p>
                <textarea
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    padding: "10px",
                    resize: "vertical",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    outline: "none",
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    overflowX: "auto",
                  }}
                  value={array_templates.map((item) => item.name).join("\n")}
                  onChange={handle_child_textarea_change}
                  placeholder={
                    language === "eng" ? "Child items..." : "Item anak..."
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <p
                  className="text_medium margin_bottom"
                  style={{ fontWeight: "bold" }}
                >
                  {dict?.["Shown if"]?.[language] || "Parent Value"}
                </p>
                <textarea
                  style={{
                    width: "100%",
                    minHeight: "200px",
                    padding: "10px",
                    resize: "vertical",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    outline: "none",
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    overflowX: "auto",
                  }}
                  value={array_templates.map((item) => item.parent).join("\n")}
                  onChange={handle_parent_textarea_change}
                  placeholder={
                    language === "eng" ? "Parent items..." : "Item induk..."
                  }
                />
              </div>
            </div>
          ) : (
            // --- SINGLE TEXTAREA MODE (NO REFERENCE) ---
            <textarea
              style={{
                width: "100%",
                minHeight: "200px",
                padding: "10px",
                resize: "vertical",
                borderRadius: "8px",
                border: "1px solid #ddd",
                outline: "none",
              }}
              value={array_templates.map((item) => item.name).join("\n")}
              onChange={(e) => {
                const lines = e.target.value.split("\n");
                const new_array = lines.map((line) => ({
                  name: line,
                  parent: "",
                }));
                set_value_properties({
                  key: "field_editable",
                  value: {
                    ...field_editable,
                    array_templates: new_array,
                  },
                });
              }}
              placeholder={
                language === "eng"
                  ? "Enter items separated by new line..."
                  : "Masukkan item dipisahkan dengan baris baru..."
              }
            />
          )
        ) : (
          // --- SINGLE INPUT LIST MODE ---
          <>
            <div
              ref={array_templates_ref}
              id="array_templates_input"
              style={{ maxHeight: "277px", overflowY: "auto" }}
            >
              {/* Top Drop Zone */}
              <div
                className={`drop_zone ${
                  drag_source_index !== null ? "is_dragging" : ""
                } ${drop_target_index === 0 ? "is_active" : ""}`}
                onDragOver={(e) => handle_drag_over(e, 0)}
                onDrop={(e) => handle_drop(e, 0)}
              >
                <div className="drop_indicator"></div>
              </div>

              {array_templates.map((item, child_index) => {
                const unique_key = item.name
                  ? `${item.name}`
                  : `item_${child_index}`;

                return (
                  <div
                    key={child_index}
                    className="folder_wrapper"
                    data-id={unique_key}
                  >
                    {parent_array_templates_key ? (
                      // --- PARENT / CHILD VARIANT ---
                      <div
                        className={`two_container_auto ${
                          drag_source_index === child_index
                            ? "is_dragging_source"
                            : ""
                        }`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {/* DRAG HANDLE */}
                          <div
                            className="button_small button_white"
                            style={{
                              height: "100%",
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "30px",
                              // Prevent Text Selection in Chrome
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              touchAction: "none",
                            }}
                            draggable={true}
                            onDragStart={(e) =>
                              handle_drag_start(e, child_index)
                            }
                            onDragEnd={handle_drag_end}
                          >
                            <img
                              alt="menu"
                              src={menu_icon}
                              height={12}
                              draggable={false}
                              style={{ pointerEvents: "none" }}
                            />
                          </div>

                          {/* INPUT */}
                          <input
                            ref={(el) => {
                              if (
                                el &&
                                child_index === array_templates.length - 1 &&
                                should_focus_ref.current
                              ) {
                                setTimeout(() => {
                                  el.focus();
                                  should_focus_ref.current = false;
                                }, 0);
                              }
                            }}
                            value={item.name}
                            onChange={(event) => {
                              const new_array = [...array_templates];
                              new_array[child_index] = {
                                ...new_array[child_index],
                                name: event.target.value,
                              };

                              set_value_properties({
                                key: "field_editable",
                                value: {
                                  ...field_editable,
                                  array_templates: new_array,
                                },
                              });
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter")
                                handle_add_array_template();
                            }}
                          />
                        </div>

                        <div className="two_container_auto">
                          <select
                            value={item.parent}
                            onChange={(event) => {
                              const new_array = [...array_templates];
                              new_array[child_index] = {
                                ...new_array[child_index],
                                parent: event.target.value,
                              };

                              set_value_properties({
                                key: "field_editable",
                                value: {
                                  ...field_editable,
                                  array_templates: new_array,
                                },
                              });
                            }}
                          >
                            <option>Pilih</option>
                            {opsi_parent_array.map((parent, p_idx) => (
                              <option key={p_idx} value={parent?.name}>
                                {parent?.name}
                              </option>
                            ))}
                          </select>
                          <div className="text_right">
                            <button
                              className="button_circle_small red_container margin_right"
                              onClick={() => {
                                const updated = array_templates.filter(
                                  (_, i) => i !== child_index,
                                );
                                set_value_properties({
                                  key: "field_editable",
                                  value: {
                                    ...field_editable,
                                    array_templates: updated,
                                  },
                                });
                              }}
                            >
                              X
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // --- SIMPLE VARIANT ---
                      <div
                        className={`two_container_auto ${
                          drag_source_index === child_index
                            ? "is_dragging_source"
                            : ""
                        }`}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {/* DRAG HANDLE */}
                          <div
                            className="button_small button_white"
                            style={{
                              height: "100%",
                              cursor: "grab",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: "30px",
                              // Prevent Text Selection in Chrome
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              touchAction: "none",
                            }}
                            draggable={true}
                            onDragStart={(e) =>
                              handle_drag_start(e, child_index)
                            }
                            onDragEnd={handle_drag_end}
                          >
                            <img
                              alt="menu"
                              src={menu_icon}
                              height={12}
                              draggable={false}
                              style={{ pointerEvents: "none" }}
                            />
                          </div>
                          <input
                            ref={(el) => {
                              if (
                                el &&
                                child_index === array_templates.length - 1 &&
                                should_focus_ref.current
                              ) {
                                setTimeout(() => {
                                  el.focus();
                                  should_focus_ref.current = false;
                                }, 0);
                              }
                            }}
                            value={item.name}
                            onChange={(event) => {
                              const new_array = [...array_templates];
                              new_array[child_index] = {
                                ...new_array[child_index],
                                name: event.target.value,
                              };

                              set_value_properties({
                                key: "field_editable",
                                value: {
                                  ...field_editable,
                                  array_templates: new_array,
                                },
                              });
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                handle_add_array_template();
                              }
                            }}
                          />
                        </div>

                        <div className="text_right">
                          <button
                            className="button_circle_small red_container margin_right"
                            onClick={() => {
                              const updated_array = array_templates.filter(
                                (_, index) => index !== child_index,
                              );
                              set_value_properties({
                                key: "field_editable",
                                value: {
                                  ...field_editable,
                                  array_templates: updated_array,
                                },
                              });
                            }}
                          >
                            X
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Drop Zone (Between items) */}
                    <div
                      className={`drop_zone ${
                        drag_source_index !== null ? "is_dragging" : ""
                      } ${
                        drop_target_index === child_index + 1 ? "is_active" : ""
                      }`}
                      onDragOver={(e) => handle_drag_over(e, child_index + 1)}
                      onDrop={(e) => handle_drop(e, child_index + 1)}
                    >
                      <div className="drop_indicator"></div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text_center margin_top">
              <button
                className="button_circle background_black text_big"
                onClick={handle_add_array_template}
              >
                +
              </button>
            </div>
          </>
        )}
      </section>

      <style>{`
        .folder_wrapper {
          position: relative;
          will-change: transform;
          margin-bottom: 8px; 
        }

        .is_dragging_source {
          opacity: 0.4;
        }

        .drop_zone {
          height: 12px; 
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .drop_zone.is_dragging::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          width: 100%;
          height: 100px; 
          transform: translateY(-50%); 
          z-index: 100;
        }

        .drop_indicator {
          width: 100%;
          height: 4px;
          background-color: #007bff;
          border-radius: 4px;
          transform: scaleX(0);
          transition: transform 0.2s ease;
          pointer-events: none;
        }

        .drop_zone.is_active .drop_indicator {
          transform: scaleX(1);
        }
      `}</style>
    </main>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, {
  set_value_properties,
})(SET_AT);
